import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, EngineKeyline, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { fetchCalendar, scheduleContent } from '@/data/sources-social';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { channelLabel } from '@/data/studio-layouts';
import { PublishStateChip } from './PublishStateChip';
import type { CalendarEntry } from '@/types/social';

/* Content calendar — a VIEW over scheduled_for with reschedule + remove
 * controls (add-to-calendar happens in the studio once a draft is approved).
 * Calendar placement is LOCAL scheduling only, NOT publication: every entry
 * shows its honest publish posture and nothing is ever shown as published
 * (no live connector exists — G-SOC-3). Reschedule / remove reuse the same
 * PATCH /v1/social/content/{id}/schedule path (null clears the placement). */

const dtCls =
  'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

export function ContentCalendarPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Domain isolation (rules 1,6): scope the calendar to the active 'Viewing'
  // domain; projectId in the queryKey refetches + drops the stale view on switch.
  const { active } = useActiveDomain();
  const projectId = active?.projectId ?? null;
  const q = useQuery({
    queryKey: ['social-calendar', projectId],
    queryFn: () => fetchCalendar(undefined, undefined, projectId),
  });

  const [editing, setEditing] = useState<number | null>(null);
  const [when, setWhen] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const mutate = useMutation({
    mutationFn: (v: { id: number; when: string | null }) => scheduleContent(v.id, v.when),
    onSuccess: (_r, v) => {
      setEditing(null);
      setNotice(v.when ? 'Rescheduled on the calendar (local placement only).' : 'Removed from the calendar.');
      void qc.invalidateQueries({ queryKey: ['social-calendar'] });
      void qc.invalidateQueries({ queryKey: ['social-content'] });
    },
  });

  const entries = q.data?.entries ?? [];
  const startEdit = (e: CalendarEntry) => {
    setNotice(null);
    setEditing(e.id);
    setWhen(e.scheduledFor ? e.scheduledFor.replace(' ', 'T').slice(0, 16) : '');
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Content calendar"
        subtitle="Scheduling places content on this calendar locally. Publishing to a provider is a separate, owner-gated step — nothing here is published."
        actions={<Badge tone="ai">Social</Badge>}
      />
      <EngineKeyline className="mb-6" />
      {notice && <p className="mb-3 text-xs text-success">{notice}</p>}
      {mutate.isError && <p className="mb-3 text-xs text-danger">Could not update the calendar. Try again.</p>}
      {q.isLoading ? (
        <LoadingState rows={4} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : entries.length === 0 ? (
        <EmptyState
          title="Nothing scheduled"
          teach="Approve a draft in the studio and give it a date — scheduled items appear here with an honest publish status."
        />
      ) : (
        <div className="space-y-2">
          {entries.map((e) => (
            <Card key={e.id} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  className="min-w-0 flex-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
                  aria-label={`Open ${e.title || 'untitled'} in the studio`}
                  onClick={() => navigate(`/social/content-studio/${e.id}`)}
                >
                  <p className="truncate text-sm font-medium text-text-primary">{e.title || 'Untitled'}</p>
                  <p className="text-xs text-text-tertiary">{channelLabel(e.channel)} &middot; {e.scheduledFor}</p>
                </button>
                <div className="flex flex-wrap items-center gap-2">
                  <PublishStateChip publishState={e.publishState} providerRef={null} />
                  <Button variant="secondary" onClick={() => (editing === e.id ? setEditing(null) : startEdit(e))}>
                    {editing === e.id ? 'Close' : 'Reschedule'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => mutate.mutate({ id: e.id, when: null })}
                    disabled={mutate.isPending}
                  >
                    Remove
                  </Button>
                </div>
              </div>
              {editing === e.id && (
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
                  <input
                    type="datetime-local"
                    className={dtCls}
                    value={when}
                    onChange={(ev) => setWhen(ev.target.value)}
                    aria-label="New date and time"
                  />
                  <Button
                    variant="ai"
                    onClick={() => mutate.mutate({ id: e.id, when: when ? when.replace('T', ' ') + ':00' : null })}
                    disabled={mutate.isPending || !when}
                  >
                    Save date
                  </Button>
                  <p className="w-full text-[11px] text-text-tertiary">
                    Local calendar placement only — this does not publish to any provider.
                  </p>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
